import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sun, Sparkles, Clock, CheckCircle, Leaf, Shield, Phone, Gift } from 'lucide-react';

export const SpringPromoBanner = () => {
  return (
    <section 
      className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-primary via-accent to-primary py-3 overflow-hidden" 
      data-testid="spring-promo-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Gift className="w-6 h-6 text-white" />
              <span className="font-barlow font-bold text-white uppercase tracking-wider text-lg">
                Limited Time — $75 Off Your First Package + Free Property Assessment
              </span>
            </div>
            <div className="hidden md:flex items-center gap-4 text-white/90 font-manrope text-sm">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Limited Availability - Book Now
              </span>
            </div>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-primary hover:bg-stone-100 font-barlow font-bold uppercase tracking-wider py-2 px-4 text-sm transition-colors"
            data-testid="spring-promo-cta"
          >
            Claim Your Free Assessment
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export const SpringPromoSection = () => {
  const coreServices = [
    {
      icon: Sun,
      title: 'Full-Season Lawn Care',
      description: 'Complete Milwaukee lawn care package including weekly mowing, edging, trimming, and seasonal treatments. Keep your Menomonee Falls or Milwaukee property looking pristine year-round.',
      benefits: ['Weekly mowing & edging', 'String trimming', 'Debris cleanup', 'Seasonal fertilization'],
    },
    {
      icon: Leaf,
      title: 'Landscaping & Design',
      description: 'Transform your outdoor space with professional Menomonee Falls landscaping services. From garden beds to complete landscape makeovers, we bring your vision to life.',
      benefits: ['Custom design consultation', 'Plant selection & installation', 'Bed maintenance', 'Mulching & edging'],
    },
    {
      icon: Sparkles,
      title: 'Hardscape Installation',
      description: 'Premium hardscape installation for patios, walkways, retaining walls, and outdoor living spaces. Quality craftsmanship for lasting beauty.',
      benefits: ['Patio & walkway design', 'Retaining walls', 'Fire pit areas', 'Outdoor kitchens'],
    },
  ];

  const includedServices = [
    'Full property assessment',
    'Custom lawn care plan',
    'Residential lawn maintenance',
    'Commercial property services',
    'Hardscape consultation',
    'Snow removal planning',
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-accent/10 via-white to-primary/10" data-testid="spring-promo-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
            <Gift className="w-5 h-5" />
            <span className="font-barlow font-semibold uppercase tracking-wider text-sm">Summer Special — Limited Time Offer</span>
          </div>
          <h2 className="font-barlow font-bold text-3xl md:text-4xl lg:text-5xl text-stone-900 uppercase mb-4">
            $75 Off Your First Package + Free Property Assessment
          </h2>
          <p className="font-manrope text-stone-600 text-lg max-w-3xl mx-auto">
            Get $75 off your first package with a free, no-obligation property assessment — limited availability.
            <strong className="text-primary"> Top-rated Milwaukee lawn care and Menomonee Falls landscaping services.</strong>
          </p>
        </div>

        {/* Offer Details Box */}
        <div className="bg-white border-2 border-primary rounded-lg p-6 md:p-8 mb-12 max-w-3xl mx-auto">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-barlow font-bold text-xl uppercase text-stone-900 mb-2">Summer Savings Package</h3>
              <p className="font-manrope text-stone-600">
                <strong>Get $75 off your first package</strong> with a free, no-obligation property assessment — limited availability.
                We're SBE-certified, locally owned, and committed to quality. Perfect for homeowners in Milwaukee, Menomonee Falls, Brookfield, and surrounding areas.
              </p>
            </div>
          </div>
          <div className="bg-accent/20 p-4 rounded-lg mb-6">
            <p className="font-barlow font-bold text-primary uppercase tracking-wider text-sm flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Call (414) 795-3610 or claim your assessment online – we respond within 24 hours!
            </p>
          </div>
          <div className="border-t border-stone-200 pt-6">
            <p className="font-barlow font-semibold uppercase tracking-wider text-sm text-stone-700 mb-3">Our Services Include:</p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {includedServices.map((service, i) => (
                <li key={i} className="flex items-center gap-2 text-stone-700 font-manrope text-sm">
                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                  {service}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {coreServices.map((service, index) => (
            <div 
              key={index}
              className="bg-white p-6 rounded-lg shadow-lg border-2 border-primary/20 hover:border-primary transition-colors"
              data-testid={`spring-service-${index}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <service.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-barlow font-bold text-lg uppercase text-stone-900">{service.title}</h3>
              </div>
              <p className="font-manrope text-stone-600 text-sm mb-4">{service.description}</p>
              <ul className="space-y-2">
                {service.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-center gap-2 text-stone-700 font-manrope text-sm">
                    <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="text-center bg-primary/5 rounded-lg p-8 border border-primary/20">
          <h3 className="font-barlow font-bold text-2xl uppercase text-stone-900 mb-4">
            Claim Your $75 Off + Free Assessment Today
          </h3>
          <p className="font-manrope text-stone-600 mb-6 max-w-xl mx-auto">
            Get $75 off your first package with a free, no-obligation property assessment — limited availability.
            Serving Milwaukee, Menomonee Falls, and all of Metro Milwaukee.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center gap-2 bg-primary text-white hover:bg-primary-600 shadow-truck active:translate-y-[2px] active:shadow-none transition-all font-barlow font-bold uppercase tracking-wider py-4 px-8 text-lg"
            data-testid="spring-promo-main-cta"
          >
            Claim Your Free Assessment
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="mt-4 font-manrope text-stone-600 text-sm flex items-center justify-center gap-2">
            <Clock className="w-4 h-4" />
            Limited availability — we respond within 24 hours.
          </p>
        </div>
      </div>
    </section>
  );
};
